import Button from '@mui/material/Button';
import { default as MuiDialog } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as React from 'react';
import { type ReactElement } from 'react';
import { useAlert } from '../alert/AlertProvider';
import DialogHeader from "./DialogHeader";
import ReadOnlyFieldset from "./ReadOnlyFieldset";

export interface DialogProps {
    open: boolean;
    title: string;
    formId?: string;
    formIsDirty?: boolean;
    children: React.ReactNode;
    loading?: boolean;
    saving?: boolean;
    saveButtonTitle?: string;
    cancelButtonTitle: string;
    onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
    pad?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    readOnly?: boolean;
    // Navigation that must keep working when the dialog is read-only — in practice a tab bar.
    //
    // It has to be a separate slot rather than part of children because read-only is enforced with a disabled
    // fieldset, and the HTML spec disables EVERY control inside one. MUI renders each Tab as a <button>, so a
    // read-only dialog lost the ability to change tabs and you could only ever see the first one. Nothing in
    // CSS can undo that — it is the disabled property, not styling — so the tab bar has to sit outside the
    // fieldset entirely.
    //
    // The rule this encodes: in a read-only dialog, navigation stays live and anything that edits goes dead.
    // A caller using this must render its TabContext (or equivalent provider) AROUND <Dialog>, so that both
    // this slot and children sit inside it. Context flows through the dialog's portal normally.
    tabs?: React.ReactNode;
}

const Dialog = ({ open, title, formId, formIsDirty, children, loading = false, saving = false, saveButtonTitle, cancelButtonTitle, onCancel: onCancel, pad = true, onKeyDown, readOnly, tabs, ...rest }: DialogProps): ReactElement => {
    const alert = useAlert();

    const handleCancel = async (event: React.MouseEvent<HTMLButtonElement>) => {
        // A read-only dialog has nothing to discard, so it must never ask. The condition used to be
        // `!formIsDirty && !readOnly`, which meant read-only dialogs always prompted.
        if (!formIsDirty || readOnly) {
            onCancel(event);
            return;
        }

        const agree = await alert({
            title: "Discard Changes",
            message: "Are you sure you want to discard your changes?",
            destructive: true,
            agreeButtonTitle: "Discard",
            closeButtonTitle: "Cancel",
        });

        if (agree) {
            onCancel(event);
        }
    }

    return (
        <React.Fragment>
            <MuiDialog
                open={open}
                onClose={handleCancel}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
                maxWidth={false}
                fullWidth={false}
                slotProps={{
                    paper: {
                        sx: { width: "fit-content", maxWidth: "calc(100vw - 64px)" },
                        onKeyDown: onKeyDown
                    },
                }}
                {...rest}
            >
            <DialogHeader title={title} onClose={handleCancel} />
            <DialogContent id="dialog-description" sx={{ p: 0 }}>
                {/* Deliberately outside the fieldset below — see the `tabs` prop. Navigation has to survive
                    read-only or the user can only ever see the tab that happened to be open. */}
                {tabs}
                {/* Read-only is handled once, by ReadOnlyFieldset — Cancel and Close sit in DialogActions,
                    outside it, and stay usable. The tabs above are deliberately outside too; see the `tabs`
                    prop. */}
                <ReadOnlyFieldset readOnly={readOnly} pad={pad ? 2 : 0}>
                    {children}
                </ReadOnlyFieldset> {/* There's a quirk with padding on DialogContent collapsing the top */}
            </DialogContent>
            <DialogActions sx={{ pb: 2, pr: 2 }}>
                <Button variant="outlined" onClick={handleCancel}>{cancelButtonTitle}</Button>
                {saveButtonTitle && !readOnly &&
                    <Button variant="contained" type="submit" form={formId} disabled={!formIsDirty} loading={saving}>{saveButtonTitle}</Button>
                }
            </DialogActions>
        </MuiDialog>                                              
        </React.Fragment >
    );
}

export default Dialog;
